// src/components/paymentTracking/CardDeclineHistoryPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Alert,
  Chip,
  Tooltip,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import apiClient from "../../services/EnhancedApiClient";
import { useEnvironment } from "../../context/EnvironmentContext";

const CardDeclineHistoryPanel = () => {
  const [declineHistory, setDeclineHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Get environment from context
  const { environment } = useEnvironment();

  // Convert to useCallback to properly handle dependencies
  const fetchDeclineHistory = useCallback(
    async (force = false) => {
      try {
        if (force) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        console.log(
          `Fetching card decline history for ${environment} environment`
        );
        const response = await apiClient.fetchCardDeclineHistory(force);

        if (response.declines && Array.isArray(response.declines)) {
          setDeclineHistory(response.declines);
        } else {
          console.warn("Invalid decline history format:", response);
          setDeclineHistory([]);
        }

        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        console.error("Error fetching card decline history:", err);
        setError("Failed to load card decline history");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [environment]
  ); // Add environment to dependencies

  // Fetch data on component mount and when environment changes
  useEffect(() => {
    fetchDeclineHistory(true); // Force refresh when environment changes
  }, [environment, fetchDeclineHistory]); // Fixed typo in 'environment' and added fetchDeclineHistory

  const handleRefresh = () => {
    fetchDeclineHistory(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get display data with pagination
  const getPagedData = () => {
    return declineHistory.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  const formatAmount = (decline) => {
    const amount =
      decline?.amount ||
      decline?.billingInfo?.amount ||
      decline?.billingInfo?.orderTotal ||
      0;

    if (amount <= 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not available
        </Typography>
      );
    }

    return (
      <Box display="flex" alignItems="center">
        <Typography variant="body2">${amount.toFixed(2)}</Typography>
      </Box>
    );
  };

  return (
    <Paper id="panel-card-decline-history" sx={{ p: 3, mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" component="h2">
          Credit Card Decline History
          {environment === "staging" && (
            <Chip label="STAGING" size="small" color="warning" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Box display="flex" alignItems="center">
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Individual credit card declines with transaction details and gateway
          responses.
        </Typography>
      </Box>

      {loading && !refreshing ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {declineHistory.length} card declines recorded{" "}
              {environment === "staging" ? "in staging" : ""}
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Decline Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {declineHistory.length > 0 ? (
                  getPagedData().map((decline) => (
                    <TableRow
                      key={decline.declineId}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {new Date(decline.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>{decline.email || "Unknown"}</TableCell>
                      <TableCell>
                        <Tooltip title="Transaction Amount">
                          {formatAmount(decline)}
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <code>{decline.ipAddress || "Unknown"}</code>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            decline.gatewayResponse ||
                            decline.declineReason ||
                            "Unknown Error"
                          }
                          arrow
                        >
                          <Chip
                            icon={<ErrorIcon fontSize="small" />}
                            label={
                              decline.gatewayResponse ||
                              decline.declineReason ||
                              "Unknown Error"
                            }
                            size="small"
                            color="error"
                            sx={{
                              maxWidth: "350px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No card decline history available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={declineHistory.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
};

export default CardDeclineHistoryPanel;
